// Third Party library imports
import { Route } from '@angular/router';

// Zopup Library imports
import {
    AuthConfirmationRequiredComponent,
    AuthForgotPasswordComponent,
    AuthGuard,
    AuthResetPasswordComponent,
    AuthSignOutComponent,
    AuthSignUpComponent,
    BranchGuard,
    CompanyBranchSelectionComponent,
    NoAuthGuard,
    NoAuthChildGuard,
    TenantGuard,
    TenantSelectionComponent,
    UserProfileComponent,
} from '@zopup/zop-auth';
import { LayoutComponent } from '@zopup/zop-ux-layout';

// Internal project imports
import { initialDataResolver } from './app.resolvers';

export const appRoutes: Route[] = [
    // Redirect empty path to '/site'
    { path: '', pathMatch: 'full', redirectTo: '/home' },
    // Guest routes (NoAuthGuard)
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthChildGuard],
        component: LayoutComponent,
        data: { layout: 'empty' },
        children: [
            {
                path: 'sign-in',
                loadComponent: () => import('@zopup/zop-auth').then(m => m.AuthSignInComponent),
            },
            {
                path: 'confirmation-required',
                loadComponent: () => AuthConfirmationRequiredComponent,
            },
            {
                path: 'forgot-password',
                loadComponent: () => AuthForgotPasswordComponent,
            },
            {
                path: 'reset-password',
                loadComponent: () => AuthResetPasswordComponent,
            },
            {
                path: 'sign-up',
                loadComponent: () => AuthSignUpComponent,
            },
        ],
    },

    // Keycloak protected routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'tenant-selection',
                loadComponent: () => TenantSelectionComponent,
            },
            {
                path: 'tenant-create',
                loadComponent: () =>
                    import('@zopup/zop-ux-onboarding').then(m => m.TenantCreateComponent),
            },
            {
                path: 'sign-out',
                loadComponent: () => AuthSignOutComponent,
            },
        ],
    },

    {
        path: 'user',
        canActivate: [BranchGuard],
        canActivateChild: [BranchGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'profile',
                loadComponent: () => UserProfileComponent,
            },
        ],
    },

    {
        path: '',
        canActivate: [TenantGuard],
        canActivateChild: [TenantGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'branch-selection',
                loadComponent: () => CompanyBranchSelectionComponent,
            },
            {
                path: 'package-selection',
                loadComponent: () =>
                    import('@zopup/zop-ux-onboarding').then(m => m.PackageSelectionComponent),
            },
            {
                path: 'company-create',
                loadComponent: () =>
                    import('@zopup/zop-ux-onboarding').then(m => m.CompanyCreateComponent),
            },
        ],
    },

    {
        path: '',
        canActivate: [TenantGuard],
        canActivateChild: [TenantGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'subscription',
                loadChildren: () =>
                    import('@zopup/zop-ux-tenant-account').then(m => m.zopUxTenantAccountRoutes),
            },
        ],
    },

    {
        path: '',
        canActivate: [BranchGuard],
        canActivateChild: [BranchGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'changelog',
                outlet: 'sidebar',
                loadChildren: () => import('./components/change-log/change-log.routing'),
            },
            {
                path: 'home',
                loadChildren: () => import('./home/home.routing'),
            },
            {
                path: 'settings',
                loadChildren: () => import('./components/settings/settings.routes'),
            },
            {
                path: 'activity-history',
                outlet: 'sidebar',
                loadComponent: () =>
                    import('@zopup/zop-ux-audit').then(m => m.AuditHistoryComponent),
            },
            {
                path: 'reports',
                loadChildren: () => import('@zopup/zop-ux-reports').then(m => m.zopUxReportsRoutes),
            },
            {
                path: 'dashboards',
                loadChildren: () =>
                    import('@zopup/zop-ux-dashboards').then(m => m.zopUxDashboardsRoutes),
            },
        ],
    },

    {
        path: 'finance',
        component: LayoutComponent,
        canActivate: [BranchGuard],
        canActivateChild: [BranchGuard],
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'account-ledgers',
                loadChildren: () =>
                    import('@zopup/zop-ux-finance').then(m => m.zopUxAccountLedgerRoutes),
            },
            {
                path: 'expense-vouchers',
                loadChildren: () =>
                    import('@zopup/zop-ux-finance').then(m => m.zopUxExpenseVouchersRoutes),
            },
            {
                path: 'expense-claim-vouchers',
                loadChildren: () =>
                    import('@zopup/zop-ux-finance').then(m => m.zopUxExpenseClaimVouchersRoutes),
            },
            {
                path: 'journal-vouchers',
                loadChildren: () =>
                    import('@zopup/zop-ux-finance').then(m => m.zopUxJournalVouchersRoutes),
            },
            {
                path: 'payment-vouchers',
                loadChildren: () =>
                    import('@zopup/zop-ux-finance').then(m => m.zopUxPaymentVouchersRoutes),
            },
            {
                path: 'receipt-vouchers',
                loadChildren: () =>
                    import('@zopup/zop-ux-finance').then(m => m.zopUxReceiptVouchersRoutes),
            },
            // Finance
            {
                path: 'accounts',
                loadChildren: () =>
                    import('@zopup/zop-ux-finance').then(m => m.zopUxAccountsRoutes),
            },
        ],
    },

    {
        path: 'inventory',
        component: LayoutComponent,
        canActivate: [BranchGuard],
        canActivateChild: [BranchGuard],
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'items',
                loadChildren: () => import('@zopup/zop-ux-inventory').then(m => m.zopUxItemsRoutes),
            },
            {
                path: 'vouchers',
                loadChildren: () =>
                    import('@zopup/zop-ux-inventory').then(m => m.zopUxVouchersRoutes),
            },
            {
                path: 'movements',
                loadChildren: () =>
                    import('@zopup/zop-ux-inventory').then(m => m.zopUxMovementRoutes),
            },
        ],
    },
    {
        path: 'assets',
        component: LayoutComponent,
        canActivate: [BranchGuard],
        canActivateChild: [BranchGuard],
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: '',
                loadChildren: () =>
                    import('@zopup/zop-ux-inventory').then(m => m.zopUxAssetsRoutes),
            },
        ],
    },
    {
        path: 'projects',
        component: LayoutComponent,
        canActivate: [BranchGuard],
        canActivateChild: [BranchGuard],
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: '',
                loadChildren: () =>
                    import('@zopup/zop-ux-project').then(m => m.zopUxProjectsRoutes),
            },
        ],
    },
    {
        path: 'work-management',
        component: LayoutComponent,
        canActivate: [BranchGuard],
        canActivateChild: [BranchGuard],
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'work-orders',
                loadChildren: () =>
                    import('@zopup/zop-ux-manufacturer').then(m => m.zopUxWorkOrderRoutes),
            },
            {
                path: 'work-plans',
                loadChildren: () =>
                    import('@zopup/zop-ux-manufacturer').then(m => m.zopUxWorkPlanRoutes),
            },
            {
                path: 'work-definitions',
                loadChildren: () =>
                    import('@zopup/zop-ux-manufacturer').then(m => m.zopUxWorkDefinitionRoutes),
            },
            {
                path: 'bom',
                loadChildren: () =>
                    import('@zopup/zop-ux-manufacturer').then(m => m.zopUxBomRoutes),
            },
            {
                // Project Material Requirements — see
                // documents/Requirements/Manufacturing/BOM-Project-PO-Features-Adapted.md Part 2.
                path: 'material-requirements',
                loadChildren: () =>
                    import('@zopup/zop-ux-manufacturer').then(m => m.zopUxProjectMaterialRequirementRoutes),
            },
            {
                path: 'alternative-groups',
                loadChildren: () =>
                    import('@zopup/zop-ux-manufacturer').then(m => m.zopUxAlternativeGroupRoutes),
            },
            {
                path: 'maintenance-requests',
                loadChildren: () =>
                    import('@zopup/zop-ux-manufacturer').then(m => m.zopUxMaintenanceRequestRoutes),
            },
            {
                path: '',
                loadChildren: () =>
                    import('@zopup/zop-ux-maintenance').then(m => m.zopUxMaintenanceRoutes),
            },
        ],
    },
    {
        path: 'calibration',
        component: LayoutComponent,
        canActivate: [BranchGuard],
        canActivateChild: [BranchGuard],
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'work-orders',
                loadChildren: () =>
                    import('@zopup/zop-ux-manufacturer').then(m => m.zopUxCalibrationWorkOrderRoutes),
            },
        ],
    },
    {
        path: 'verified',
        component: LayoutComponent,
        canActivate: [BranchGuard],
        canActivateChild: [BranchGuard],
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: '',
                loadChildren: () => import('@zopup/zop-ux-catalogue').then(m => m.zopUxScanRoutes),
            },
        ],
    },
    {
        path: 'purchase',
        component: LayoutComponent,
        canActivate: [BranchGuard],
        canActivateChild: [BranchGuard],
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'vendors',
                loadChildren: () => import('@zopup/zop-ux-vendors').then(m => m.zopUxVendorsRoutes),
            },
            {
                path: 'dashboard',
                loadComponent: () => import('@zopup/zop-ux-purchase').then(m => m.PurchaseDashboardComponent),
            },
            {
                path: 'orders',
                loadChildren: () => import('@zopup/zop-ux-purchase').then(m => m.zopUxPurchaseOrdersRoutes),
            },
            {
                path: 'goods-receipt-notes',
                loadChildren: () =>
                    import('@zopup/zop-ux-purchase').then(m => m.zopUxGoodsReceiptNotesRoutes),
            },
        ],
    },
    {
        path: 'documents',
        component: LayoutComponent,
        canActivate: [BranchGuard],
        canActivateChild: [BranchGuard],
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: '',
                loadChildren: () =>
                    import('@zopup/zop-ux-documents').then(m => m.zopUxDocumentsRoutes),
            },
        ],
    },
    {
        path: 'alerts',
        component: LayoutComponent,
        canActivate: [BranchGuard],
        canActivateChild: [BranchGuard],
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: '',
                loadChildren: () => import('@zopup/zop-ux-alerts').then(m => m.zopUxAlertsRoutes),
            },
        ],
    },
    {
        path: 'task-boards',
        component: LayoutComponent,
        canActivate: [BranchGuard],
        canActivateChild: [BranchGuard],
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: '',
                loadChildren: () => import('@zopup/zop-ux-tasks').then(m => m.zopUxTasksRoutes),
            },
        ],
    },
    {
        path: 'audit-log',
        canActivate: [BranchGuard],
        canActivateChild: [BranchGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('@zopup/zop-ux-audit').then(m => m.AuditTimelineComponent),
            },
        ],
    },
    {
        path: 'activity-history',
        outlet: 'sidebar',
        loadComponent: () => import('@zopup/zop-ux-audit').then(m => m.AuditHistoryComponent),
    },
    {
        path: 'customer-quick-view',
        outlet: 'sidebar',
        loadComponent: () => import('@zopup/zop-ux-sales').then(m => m.CustomerQuickViewComponent),
    },
    {
        path: 'documents-quick-view',
        outlet: 'sidebar',
        loadComponent: () =>
            import('@zopup/zop-ux-documents').then(m => m.DocumentsQuickViewComponent),
    },
    {
        path: 'agent-view',
        outlet: 'sidebar',
        loadComponent: () => import('@zopup/zop-ux-agent').then(m => m.AgentComponent),
    },
    {
        path: 'support-view',
        outlet: 'sidebar',
        loadComponent: () => import('@zopup/zop-ux-support').then(m => m.SupportComponent),
    },
];
